import { Component, inject, OnInit, AfterViewInit, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { RouterLink, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { AuthService, User } from '../../services/auth.service';
import { LoadingComponent } from '../../shared/components/loading/loading.component';

declare const google: any;

@Component({
  selector: 'app-register',
  standalone: true,
  imports: [RouterLink, ReactiveFormsModule, CommonModule, LoadingComponent],
  templateUrl: './register.component.html',
  styleUrl: './register.component.css'
})
export class RegisterComponent implements OnInit, AfterViewInit {
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  registerForm!: FormGroup;
  isStep1 = true; // İlk adım: Kod gönderme
  isLoading = false;
  errorMessage: string | null = null;
  successMessage: string | null = null;
  private isBrowser: boolean;

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.initializeForm();
  }

  ngOnInit(): void {
    // Component başlatma işlemleri
  }

  ngAfterViewInit(): void {
    // Google Sign-In'i başlat (sadece step 1'de)
    if (this.isBrowser && this.isStep1) {
      this.initializeGoogleSignIn();
      setTimeout(() => {
        this.renderGoogleButton();
      }, 100);
    }
  }

  private initializeForm(): void {
    this.registerForm = this.fb.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      phoneNumber: ['', [Validators.required, Validators.pattern(/^(\+90|0)?[0-9]{10}$/)]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      passwordConfirm: ['', [Validators.required]],
      code: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
      newsletter: [false]
    }, {
      validators: this.passwordMatchValidator
    });
  }

  // Şifre tekrarı kontrolü
  passwordMatchValidator(form: FormGroup) {
    const password = form.get('password');
    const passwordConfirm = form.get('passwordConfirm');
    
    if (password && passwordConfirm && password.value !== passwordConfirm.value) {
      passwordConfirm.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    }
    
    if (passwordConfirm && passwordConfirm.hasError('passwordMismatch')) {
      passwordConfirm.setErrors(null);
    }
    
    return null;
  }

  // 1. Adım: Doğrulama kodu gönder
  sendVerificationCode(): void {
    // İlk adımda tüm temel alanları kontrol et (kod hariç)
    const firstName = this.registerForm.get('firstName');
    const lastName = this.registerForm.get('lastName');
    const email = this.registerForm.get('email');
    const phoneNumber = this.registerForm.get('phoneNumber');
    const password = this.registerForm.get('password');
    const passwordConfirm = this.registerForm.get('passwordConfirm');

    // Tüm alanları işaretle
    firstName?.markAsTouched();
    lastName?.markAsTouched();
    email?.markAsTouched();
    phoneNumber?.markAsTouched();
    password?.markAsTouched();
    passwordConfirm?.markAsTouched();

    // Validasyon kontrolü
    if (firstName?.invalid || lastName?.invalid || email?.invalid || phoneNumber?.invalid || password?.invalid || passwordConfirm?.invalid) {
      this.errorMessage = 'Lütfen tüm alanları doldurun.';
      return;
    }

    // Şifre uyumsuzluğu kontrolü
    if (password?.value !== passwordConfirm?.value) {
      this.errorMessage = 'Şifreler eşleşmiyor.';
      return;
    }

    const emailValue = email?.value;
    this.isLoading = true;
    this.errorMessage = null;
    this.successMessage = null;

    this.authService.sendVerificationCode(emailValue).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.successMessage = 'Doğrulama kodu e-posta adresinize gönderildi.';
        this.isStep1 = false; // 2. adıma geç
      },
      error: (error) => {
        this.isLoading = false;
        if (error.error?.message) {
          if (error.error.message === 'Email already registered.') {
            this.errorMessage = 'Bu e-posta adresi zaten kayıtlı.';
          } else if (error.error.message === 'Failure sending mail.') {
            this.errorMessage = 'E-posta gönderilemedi. Lütfen tekrar deneyin.';
          } else {
            this.errorMessage = error.error.message;
          }
        } else {
          this.errorMessage = 'Bir hata oluştu. Lütfen tekrar deneyin.';
        }
      }
    });
  }

  // 2. Adım: Kodu doğrula ve kayıt ol
  verifyAndRegister(): void {
    if (this.registerForm.invalid) {
      // Tüm alanları işaretle
      Object.keys(this.registerForm.controls).forEach(key => {
        this.registerForm.get(key)?.markAsTouched();
      });
      this.errorMessage = 'Lütfen tüm alanları doldurun.';
      return;
    }

    const formValue = this.registerForm.value;
    
    // Şifre uyumsuzluğu kontrolü
    if (formValue.password !== formValue.passwordConfirm) {
      this.errorMessage = 'Şifreler eşleşmiyor.';
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;
    this.successMessage = null;

    const registerData = {
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      email: formValue.email,
      phoneNumber: formValue.phoneNumber,
      password: formValue.password,
      code: formValue.code
    };

    this.authService.verifyCodeAndRegister(registerData).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.successMessage = 'Kayıt başarılı! Giriş sayfasına yönlendiriliyorsunuz...';
        
        // 2 saniye sonra login sayfasına yönlendir
        setTimeout(() => {
          this.router.navigate(['/login']);
        }, 2000);
      },
      error: (error) => {
        this.isLoading = false;
        
        if (error.error) {
          // Şifre validasyon hataları array olarak gelebilir
          if (Array.isArray(error.error)) {
            this.errorMessage = error.error.join(' ');
          } else if (error.error.message) {
            if (error.error.message === 'Invalid or expired verification code.') {
              this.errorMessage = 'Geçersiz veya süresi dolmuş doğrulama kodu.';
            } else if (error.error.message === 'Email already registered.') {
              this.errorMessage = 'Bu e-posta adresi zaten kayıtlı.';
            } else {
              this.errorMessage = error.error.message;
            }
          } else {
            this.errorMessage = 'Bir hata oluştu. Lütfen tekrar deneyin.';
          }
        } else {
          this.errorMessage = 'Bir hata oluştu. Lütfen tekrar deneyin.';
        }
      }
    });
  }

  // Geri dön butonu için
  goBackToStep1(): void {
    this.isStep1 = true;
    this.errorMessage = null;
    this.successMessage = null;
    this.registerForm.get('code')?.reset();
  }

  // Form alanlarına kolay erişim için getter'lar
  get firstName() { return this.registerForm.get('firstName'); }
  get lastName() { return this.registerForm.get('lastName'); }
  get email() { return this.registerForm.get('email'); }
  get phoneNumber() { return this.registerForm.get('phoneNumber'); }
  get password() { return this.registerForm.get('password'); }
  get passwordConfirm() { return this.registerForm.get('passwordConfirm'); }
  get code() { return this.registerForm.get('code'); }

  // Google Sign-In başlatma
  private initializeGoogleSignIn(): void {
    if (typeof google !== 'undefined') {
      google.accounts.id.initialize({
        client_id: '540796751930-m36rbnhi0k7dhu3c8inipse7nfvbfcd4.apps.googleusercontent.com',
        callback: (response: any) => this.handleCredentialResponse(response)
      });
    }
  }

  // Google Button'ı render et
  private renderGoogleButton(): void {
    if (typeof google !== 'undefined') {
      const buttonDiv = document.getElementById('googleSignInButtonRegister');
      if (buttonDiv) {
        google.accounts.id.renderButton(
          buttonDiv,
          { 
            theme: 'outline',
            size: 'large',
            width: buttonDiv.offsetWidth || 350,
            text: 'signup_with',
            shape: 'rectangular',
            locale: 'tr'
          }
        );
      }
    }
  }

  // Google Sign-In callback fonksiyonu
  handleCredentialResponse(response: any): void {
    // JWT token'ı decode et
    const token = response.credential;
    const payload = this.parseJwt(token);

    // Kullanıcı bilgilerini oluştur
    const user: User = {
      email: payload.email,
      firstName: payload.given_name || '',
      lastName: payload.family_name || '',
      token: token
    };

    // Kullanıcıyı kaydet ve yönlendir
    // Not: Google ile kayıt yapan kullanıcılar zaten doğrulanmış sayılır
    this.authService.setCurrentUser(user);
    this.successMessage = 'Google ile kayıt başarılı! Yönlendiriliyorsunuz...';
    
    setTimeout(() => {
      this.router.navigate(['/']);
    }, 1500);
  }

  // JWT token'ı decode etme fonksiyonu
  private parseJwt(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      return {};
    }
  }
}
